/**
 * Storefront IIFE entry: reads `window.BirdsviewTrackingConfig`, wires
 * {@link Shopware66Adapter} + {@link TrackingClient} and starts tracking.
 * Vite builds this into `public/static/birdsview-tracking/bv-tracking.js`,
 * loaded via a <script> tag (see product-detail/meta.html.twig). Shopware's
 * storefront webpack never compiles it — the plugin ships no storefront entry.
 */
import { TrackingClient } from './TrackingClient';
import { Shopware66Adapter } from './adapters/Shopware66Adapter';
import { TrackingDebugService } from './services/TrackingDebugService';
import { captureMailSeed } from './mailSeed';
import {
  BrowserDomEventService,
  BrowserFetchService,
  BrowserLocalStorageService,
  BrowserLocationService,
  BrowserTimerService,
} from './services/browser';

declare global {
  interface Window {
    BirdsviewTrackingConfig?: {
      apiUrl: string;
      /** Full storefront domain URL for this page; forwarded to the tracking API as `?domain=` for project resolution (INT-225). */
      storefrontUrl?: string;
    };
  }
}

const cfg = window.BirdsviewTrackingConfig;
const debug = new TrackingDebugService();

debug.log('bootstrap', 'storefront tracking loaded', {
  hasApiUrl: !!cfg?.apiUrl,
});

if (cfg?.apiUrl) {
  // Pre-consent by design (INT-294): consent gates the *use* of the id, not its capture, and the
  // parameter is gone from the URL by the time a visitor accepts the banner two pages later. Inside
  // the apiUrl guard, so a shop without tracking configured never gets an identifier written to its
  // visitors' devices. The try/catch is not optional: BrowserLocalStorageService is unguarded and
  // throws when site data is blocked, and this file has no other top-level error handling — an
  // uncaught throw here would kill the whole bundle, including tracking that would otherwise work.
  try {
    const captured = captureMailSeed({
      href: window.location.href,
      // Re-pass the current state rather than `null`: the storefront or a theme may have set it on
      // this history entry, and overwriting it breaks whatever restores from it on back/forward.
      replaceUrl: (url) => window.history.replaceState(window.history.state, '', url),
      storage: new BrowserLocalStorageService(),
    });

    if (captured === 'rejected') {
      debug.log('bootstrap', 'mail seed rejected by validation', {});
    }
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : String(err);
    debug.log('bootstrap', 'mail seed capture failed', { message });
  }

  const adapter = new Shopware66Adapter(
    new BrowserFetchService(),
    new BrowserDomEventService(),
    new BrowserTimerService(),
    new BrowserLocationService(),
    debug,
    cfg.storefrontUrl,
  );

  const client = new TrackingClient({
    apiUrl: cfg.apiUrl,
    storefrontUrl: cfg.storefrontUrl,
    adapter,
    debug,
  });

  client.init();
}
